/**
 * iSteelElement Main Application Component
 */

import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { SUPPORT_TYPES, defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useLivePlotPrintImages, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './api';
import type { ChartKey, MetalStudResultState } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, SECTION_NAMES, LIVE_CHART_ORDER, LIVE_PLOT_CARD_MIN_HEIGHT, LIVE_PLOT_SIZE_BY_KEY, PRINT_CHART_ASPECT, PRINT_CHART_IMAGE_OPTIONS, PRINT_CHART_ORDER } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, GenerateLinkButton, LivePlot, PrintChartImageGrid, RichSummaryReport } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  // Check for calcId first (short URL from CalcPad)
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  const initialValues: FormValues = useMemo(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'Metal Stud',
          L: '15',
          W: '16/12',
          addedWeight: '5',
          support: 1,
          sectionName: SECTION_NAMES[0].label,
          numStuds: 1,
          restraintLength: '4',
          axialLoad: '0',
          pressureHistory: '4,0,0',
          timeHistory: '0,14,30',
          u0: '0',
          v0: '0'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({ schema, defaultValues: initialValues });

  const values = watch();
  const { saveDataToDB } = useAppStorage<FormValues, MetalStudResultState>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, MetalStudResultState>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });
  const { chartRefs, printRootRef } = useLivePlotPrintImages<ChartKey, MetalStudResultState>({
    analysisResult,
    setAnalysisResult,
    getImageOptions: key => PRINT_CHART_IMAGE_OPTIONS[PRINT_CHART_ASPECT[key]],
    errorMessage: 'Unable to prepare metal stud charts for printing. Please wait for the charts to finish rendering and try again.'
  });
  const crossSectionSrc = analysisResult?.cross_section?.base64 ? `data:${analysisResult.cross_section.mime};base64,${analysisResult.cross_section.base64}` : null;

  // Load full state from IndexedDB if calcId is present
  useSavedCalculationLoader<FormValues, MetalStudResultState>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  const fields = defineFields<FormValues>([
    { kind: 'input', name: 'title', label: 'Title', helpText: 'Enter the title of the component', placeholder: 'Component Title' },
    { kind: 'input', name: 'L', label: 'Length', unit: 'ft', helpText: 'Enter the length of the component', placeholder: 'Component Length' },
    {
      kind: 'input',
      name: 'W',
      label: 'Tributary Width',
      unit: 'ft',
      helpText: 'Enter the tributary width of the component',
      placeholder: 'Component Tributary Width'
    },
    {
      kind: 'input',
      name: 'addedWeight',
      label: 'Added Weight',
      unit: 'psf',
      helpText: 'Enter the added weight on the component',
      placeholder: 'Component Added Weight'
    },
    { kind: 'dropdown', name: 'support', label: 'Support', options: SUPPORT_TYPES, helpText: 'Enter the support type...' },
    { kind: 'dropdown', name: 'sectionName', label: 'Section Name', options: SECTION_NAMES, helpText: 'Enter the Section Name...' },
    { kind: 'input', name: 'numStuds', label: 'Number of Studs', unit: '', helpText: 'Number of Studs', placeholder: 'Number of Studs' },
    { kind: 'input', name: 'restraintLength', label: 'Restraint Length', unit: 'ft', helpText: 'Restraint Length', placeholder: 'Restraint Length' },
    { kind: 'input', name: 'axialLoad', label: 'Axial Load', unit: 'kips', helpText: 'Axial Load', placeholder: 'Axial Load' },
    { kind: 'input', name: 'pressureHistory', label: 'Pressure History', unit: 'psi', helpText: 'Pressure History', placeholder: 'Pressure History' },
    { kind: 'input', name: 'timeHistory', label: 'Time History', unit: 'msec', helpText: 'Time History', placeholder: 'Time History' },
    { kind: 'input', name: 'u0', label: 'Initial Displacement', unit: 'in', helpText: 'Initial Displacement', placeholder: 'Initial Displacement' },
    { kind: 'input', name: 'v0', label: 'Initial Velocity', unit: 'in/msec', helpText: 'Initial Velocity', placeholder: 'Initial Velocity' }
  ]);
  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">Metal Stud Analysis</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex flex-row w-full mt-1 gap-2">
            <button className="submit-button" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid min-w-0 grid-rows-[auto_1fr] rounded-xl gap-y-0 w-full">
          <div className="analysis-app__results shadow-div rounded-xl m-1 min-w-0 overflow-hidden">
            {analysisResult?.summary ? <RichSummaryReport summary={analysisResult.summary} className="rounded-lg border bg-white p-4 text-[13px] leading-5 text-slate-800" /> : <div className="p-4 text-black-500 italic">{analysisResult ? 'Saved result does not include a summary. Re-run the analysis to generate it.' : 'No analysis results yet.'}</div>}
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {(analysisResult?.plotData || crossSectionSrc) && (
              <div className="analysis-app__charts-grid grid grid-cols-1 xl:grid-cols-2 items-stretch gap-3 m-2 sm:m-4 print:gap-1">
                {analysisResult?.plotData &&
                  LIVE_CHART_ORDER.map(chartKey => {
                    const plot = analysisResult.plotData[chartKey];
                    if (!plot) return null;

                    return (
                      <div key={chartKey} className="analysis-app__chart-card flex min-w-0 items-center justify-center break-inside-avoid" style={{ minHeight: LIVE_PLOT_CARD_MIN_HEIGHT }}>
                        <LivePlot
                          ref={node => {
                            chartRefs.current[chartKey] = node;
                          }}
                          plot={plot}
                          {...LIVE_PLOT_SIZE_BY_KEY[chartKey]}
                        />
                      </div>
                    );
                  })}
                {crossSectionSrc && (
                  <div className="analysis-app__chart-card flex min-w-0 items-center justify-center break-inside-avoid" style={{ minHeight: LIVE_PLOT_CARD_MIN_HEIGHT }}>
                    <div className="flex h-full w-full max-w-[30rem] flex-col items-center justify-center gap-3 bg-white p-4">
                      <div className="text-sm font-semibold text-slate-700">Cross Section</div>
                      <img src={crossSectionSrc} alt="Metal stud wall cross section" loading="lazy" className="max-h-full max-w-full object-contain rounded-none" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="print:hidden hidden lg:block"></div>
      </div>

      {/* ---- PRINT PORTAL ---- */}
      <PrintPortal>
        <div ref={printRootRef} className="print-root">
          <div className="print-a4-landscape box-border bg-white p-[1mm]">
            <div className="hidden print:grid h-full min-h-0 grid-cols-[24%_76%] border border-black print:bg-white print:text-black">
              <div className="flex flex-col h-full min-h-0">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'Metal Stud'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    <RichSummaryReport summary={analysisResult?.summary} className="text-[9.5px] leading-[1.18] text-black" />
                  </div>
                </div>
                {crossSectionSrc ? (
                  <div className="grid grid-cols-1 gap-1 border-t p-1">
                    <div className="flex h-[28mm] min-h-0 flex-col overflow-hidden border border-slate-200 bg-white p-0.5">
                      <div className="mb-0.5 text-center text-[7px] font-semibold leading-none text-slate-700">Section</div>
                      <img src={crossSectionSrc} alt="Metal stud wall cross section" loading="eager" decoding="sync" className="min-h-0 flex-1 object-contain rounded-none" />
                    </div>
                  </div>
                ) : null}

                {/* Shareable Link at Bottom */}
                <div className="grid grid-cols-2 mt-auto p-1 border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-[9px] text-blue-600 underline font-semibold hover:text-blue-800" target="_blank" rel="noopener noreferrer" title="Click to open shareable link">
                    🔗 LINK
                  </a>
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none text-blue-500">{`${APP_NAME} V${APP_VERSION}`}</div>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 overflow-hidden">
                  <PrintChartImageGrid charts={analysisResult?.charts} order={PRINT_CHART_ORDER} aspectByKey={PRINT_CHART_ASPECT} fitPage />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
